import Table, { ColumnsType } from "antd/es/table";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { PaginationInput, ProviderList } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import { getErrorMessage, logger } from "src/lib/utils";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { Form, Input, InputNumber } from "antd";
import { CheckIcon } from "src/components/icons/check-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { useModalAction } from "src/components/modal-views/context";
import { PlusIcon } from "src/components/icons/plus-icon";
import { callback } from "chart.js/dist/helpers/helpers.core";
import { EyeIcon } from "src/components/icons/eye-icon";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CopyIcon } from "src/components/icons/copy-icon";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: ProviderList;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const Provider = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [form] = Form.useForm();

  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.SELF_TERMINAL
  );
  const canViewProviders = canView || canList;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<ProviderList[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });
  const [editingKey, setEditingKey] = useState("");
  const [updatedData, setUpdatedData] = useState<ProviderList | null>(null);

  const [total, setTotal] = useState(0);
  const { openModal, closeModal } = useModalAction();
  const [isCopied, setIsCopied] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const { mutate: getList, isLoading } = useMutation(client.selfTerminal.all, {
    onSuccess: (data: any) => {
      const dataObj = data.data;
      logger.log(dataObj, "  OBJJJ");
      setTotal(dataObj.total);
      setDataSource(
        dataObj?.data.map((item: any) => ({
          key: item.id,
          id: item.id,
          live_key: item.live_key,
          live_secret: item.live_secret,
          provider_identifier: item.provider_identifier,
          provider_name: item.provider_name,
          sandbox_key: item.sandbox_key,
          sandbox_secret: item.sandbox_secret,
          updated_at: item.updated_at,
          created_at: item.created_at,
        }))
      );
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const { mutate: deleteProvider, isLoading: deleteProviderLoading } =
    useMutation(client.selfTerminal.remove, {
      onSuccess: (data: any) => {
        setAlertText("Provider Deleted");
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: updateProvider, isLoading: updateProviderLoading } =
    useMutation(client.selfTerminal.update, {
      onSuccess: (data: any) => {
        setAlertText("Provider Updated");
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  useEffect(() => {
    if (!isMounted || !canViewProviders) {
      return;
    }
    getList({
      ...searchParams,
    });
  }, [isMounted, canViewProviders, searchParams.current_page]);

  const isEditing = (record: ProviderList) => record.key === editingKey;

  const edit = (record: Partial<ProviderList> & { key: React.Key }) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit providers");
      setIsAlertOpened(true);
      return;
    }
    form.setFieldsValue({ ...record });
    setEditingKey(record.key as string);
  };

  const onView = (record: Partial<ProviderList> & { key: React.Key }) => {
    if (!canView) {
      setAlertText("You do not have permission to view provider details");
      setIsAlertOpened(true);
      return;
    }
    openModal("PROVIDER_VIEW_MODAL", {
      record,
    });
  };

  const cancel = (record: any) => {
    setEditingKey("");
    form.resetFields();
    if (record.isNew) {
      setDataSource((prev) => prev.filter((item) => item.key !== record.key));
    }
  };

  const save = async (key: React.Key) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit providers");
      setIsAlertOpened(true);
      return;
    }
    try {
      const row = (await form.validateFields()) as ProviderList;
      const selData = dataSource.filter((item) => item.key === key)[0];
      setUpdatedData(row);
      const obj = {
        ...row,
        is_active: 1,
      };
      updateProvider({ row: obj, id: key });

      return;
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  const onCallback = (bSuccess: boolean) => {
    bSuccess && getList(searchParams);
  };

  const onDelete = (key: number) => {
    if (!canDelete) {
      setAlertText("You do not have permission to delete providers");
      setIsAlertOpened(true);
      return;
    }
    openModal("GENERAL_DELETE", {
      id: key,
      callback: () => {
        deleteProvider({ id: key });
        setTimeout(() => {
          closeModal();
        }, 100);
      },
      isLoading: () => deleteProviderLoading,
    });
  };

  const addNew = () => {
    if (!canAdd) {
      setAlertText("You do not have permission to add providers");
      setIsAlertOpened(true);
      return;
    }
    openModal("ADD_NEW_PROVIDER", {
      callback: () =>
        getList({
          ...searchParams,
        }),
    });
  };

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value: any) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Name
        </p>
      ),
      dataIndex: "provider_name",
      className: "w-auto",
      key: "provider_name",
      editable: true,
      render: (value: any) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Provider Id
        </p>
      ),
      dataIndex: "provider_identifier",
      className: "w-auto",
      key: "provider_identifier",
      editable: true,
      render: (value: any) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          API Keys
        </p>
      ),
      dataIndex: "live_key",
      className: "w-auto",
      key: "live_key",
      render: (value: any, record: ProviderList) => {
        const handleCopy = (id: number, keyType: string) => {
          const copyKey = `${id}-${keyType}`;

          setIsCopied((prev: any) => ({
            ...prev,
            [copyKey]: true,
          }));

          setTimeout(() => {
            setIsCopied((prev: any) => ({
              ...prev,
              [copyKey]: false,
            }));
          }, 4000);
        };

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
                Sandbox: {record?.sandbox_key}
              </p>
              <CopyToClipboard
                text={record?.sandbox_key || ""}
                onCopy={() => handleCopy(record.id, "sandbox")}
              >
                <button
                  className={`${
                    isCopied[`${record.id}-sandbox`]
                      ? "text-green-500"
                      : "text-black"
                  } hover:underline`}
                >
                  {<CopyIcon />}
                </button>
              </CopyToClipboard>
            </div>

            <div className="flex items-center gap-2">
              <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
                Live: {value}
              </p>
              <CopyToClipboard
                text={value || ""}
                onCopy={() => handleCopy(record.id, "live")}
              >
                <button
                  className={`${
                    isCopied[`${record.id}-live`] ? "text-green-500" : "text-black"
                  } hover:underline`}
                >
                  {<CopyIcon />}
                </button>
              </CopyToClipboard>
            </div>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value: any) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {moment(value).format("YYYY-MM-DD HH:mm:ss")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated At
        </p>
      ),
      dataIndex: "updated_at",
      className: "w-auto",
      key: "updated_at",
      render: (value: any) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {moment(value).format("YYYY-MM-DD HH:mm:ss")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_: any, record: ProviderList) => {
        const editable = isEditing(record);
        if (!canView && !canDelete && !canEdit) {
          return "—";
        }
        return editable ? (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => cancel(record)}
            >
              <CloseIcon className="w-[20px] h-[20px] text-secondary" />
            </button>
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => save(record.key)}
            >
              <CheckIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            {canView && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => onView(record)}
              >
                <EyeIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
            {canDelete && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => onDelete(Number(record.key))}
              >
                <DeleteIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
            {canEdit && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-start"
                onClick={() => edit(record)}
              >
                <EditIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: ProviderList) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: "",
        editing: isEditing(record),
      }),
    };
  });

  return (
    <div className="custom-scroll rounded-[12px] w-full w-full h-full bg-gray">
      <div className="flex justify-end py-3">
        <PermissionGate
          module={[permissionConstants.SELF_TERMINAL]}
          action="can_add"
        >
          <button
            className="flex flex-row items-center justify-between px-2 sm:px-4 py-2 gap-2 bg-white rounded-[3px] flex-shrink-0 hover:bg-[#E6E8EC]"
            onClick={() => addNew()}
          >
            <p className="font-manrope text-[14px] font-medium text-primary">
              Add Provider
            </p>
            <PlusIcon className="w-[18px] h-[18px] text-primary" />
          </button>
        </PermissionGate>
      </div>
      {!canViewProviders ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] m-4 p-8 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view terminal providers.
          </p>
        </div>
      ) : (
        <div className="p-4 relative">
          <Form form={form} component={false}>
            <div className="custom-scroll">
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                loading={isLoading}
                showSorterTooltip={false}
                columns={isMobile ? mergedColumns : mergedColumns}
                dataSource={dataSource}
                rowClassName="editable-row"
                pagination={{
                  total,
                  current: searchParams.current_page,
                  onChange: onPageChange,
                  position: ["bottomCenter"],
                  defaultPageSize: LIST_PAGE_SIZE,
                  showSizeChanger: false,
                }}
              />
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default Provider;
